import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

function PrivacyPolicy() {
    return (
        <div className="legal-page">
            <header className="page-section-head">
                <h1>Legal <span className="page-section-current">Privacy Policy</span></h1>
            </header>
            <div className="legal-container">
                <p className="legal-updated">Effective Date: September 7, 2026</p>

                <section>
                    <p>
                        Trade Spot ("we," "us," or "our") operates the Trade Spot platform (the "Service").
                        This Privacy Policy explains how we collect, use, disclose, and safeguard your information
                        when you use our Service. Please read this Privacy Policy carefully. By using the Service,
                        you consent to the data practices described in this policy.
                    </p>
                    <p>
                        This Privacy Policy is part of our <Link to="/terms">Terms of Service</Link>. 
                        For information about third-party content and intellectual property, please see 
                        our <Link to="/credits">Credits & Legal</Link> page.
                    </p>
                </section>

                <section>
                    <h2>1. Information We Collect</h2>

                    <h3>1.1 Personal Information</h3>
                    <p>We may collect personally identifiable information that you voluntarily provide when registering for an account, including but not limited to:</p>
                    <ul>
                        <li>Username</li>
                        <li>Email address (verified via a confirmation link)</li>
                        <li>Password (stored as a salted hash, never in plain text)</li>
                        <li>Profile information and photo you choose to provide</li>
                        <li>
                            Delivery addresses you enter for trades or save as a default. Addresses are
                            verified against map data and stored with their coordinates.
                        </li>
                        <li>Trading preferences (middleman default, preferred shop, notification settings)</li>
                    </ul>

                    <h3>1.2 Usage Data</h3>
                    <p>We automatically collect certain information when you access the Service, including:</p>
                    <ul>
                        <li>IP address</li>
                        <li>Browser type and version</li>
                        <li>Pages visited and time spent on pages</li>
                        <li>Access times and dates</li>
                        <li>Device information</li>
                    </ul>

                    <h3>1.3 User Content</h3>
                    <p>We collect and store data you submit through the Service, including:</p>
                    <ul>
                        <li>Card collection data (haves and wants lists)</li>
                        <li>Trade requests, trade history, and per-trade event logs</li>
                        <li>
                            Card documentation photos and descriptions submitted during direct trades.
                            These are shared with your trade partner for review.
                        </li>
                        <li>
                            Sealed-package photos. These are <strong>private</strong>: they are never shown
                            to your trade partner and are only accessible to you and to site administrators
                            in the event of a dispute.
                        </li>
                        <li>Middleman reviews and ratings you submit</li>
                        <li>User preferences and settings</li>
                    </ul>

                    <h3>1.4 Address Visibility</h3>
                    <p>
                        Delivery addresses are shown only to whoever must ship to you: in a middleman trade,
                        only the shop forwarding your cards sees your address - your trade partner never
                        does. In a direct trade, your partner sees your address only after both sides have
                        completed the documentation step.
                    </p>
                </section>

                <section>
                    <h2>2. Use of Information</h2>
                    <p>We use the information we collect for the following purposes:</p>
                    <ul>
                        <li>To provide, operate, and maintain the Service</li>
                        <li>To create and manage your account</li>
                        <li>To facilitate trades between users</li>
                        <li>To communicate with you regarding your account or transactions</li>
                        <li>To send administrative information, updates, and security alerts</li>
                        <li>To respond to your inquiries and provide customer support</li>
                        <li>To analyze usage patterns and improve the Service</li>
                        <li>To detect, prevent, and address technical issues, fraud, or illegal activity</li>
                        <li>To comply with legal obligations</li>
                    </ul>
                </section>

                <section>
                    <h2>3. Disclosure of Information</h2>
                    <p>We may share your information in the following circumstances:</p>

                    <h3>3.1 With Other Users</h3>
                    <p>Certain information, including your username, public profile data, and collection information, may be visible to other users of the Service to facilitate trading functionality.</p>

                    <h3>3.2 Service Providers</h3>
                    <p>We use a small number of third-party services to operate the Service:</p>
                    <ul>
                        <li>
                            <strong>OpenStreetMap / Nominatim</strong> - when you enter a delivery or shop
                            address, it is sent to the Nominatim geocoding service to verify it and obtain
                            map coordinates. Their use is governed by the
                            {' '}<a href="https://osmfoundation.org/wiki/Privacy_Policy" target="_blank" rel="noopener noreferrer">OSM Foundation privacy policy</a>.
                        </li>
                        <li>
                            <strong>Resend</strong> - delivers our transactional email (address verification,
                            password resets). Your email address is shared with them for that purpose only.
                        </li>
                        <li>
                            <strong>Microsoft Clarity</strong> - helps us understand how the Service is used by
                            recording page views, clicks, scrolling, and anonymized session replays. Text you type
                            into forms is masked before it leaves your browser. Clarity sets its own cookies and
                            processes this data under the
                            {' '}<a href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noopener noreferrer">Microsoft Privacy Statement</a>.
                        </li>
                    </ul>
                    <p>We may share information with third-party vendors and service providers that perform services on our behalf, such as hosting, data analysis, and customer service.</p>

                    <h3>3.3 Legal Requirements</h3>
                    <p>We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).</p>

                    <h3>3.4 Business Transfers</h3>
                    <p>In the event of a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction.</p>

                    <h3>3.5 Protection of Rights</h3>
                    <p>We may disclose information when we believe disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to a government request.</p>
                </section>

                <section>
                    <h2>4. Data Security</h2>
                    <p>
                        We implement appropriate technical and organizational security measures designed to protect
                        the security of personal information we process. These measures include:
                    </p>
                    <ul>
                        <li>Encryption of passwords using industry-standard hashing algorithms (bcrypt)</li>
                        <li>Secure HTTPS connections for all data transmission</li>
                        <li>Regular security assessments and updates</li>
                        <li>Access controls limiting employee access to personal data</li>
                    </ul>
                    <p>
                        However, please note that no method of transmission over the Internet or method of
                        electronic storage is 100% secure. While we strive to use commercially acceptable means
                        to protect your personal information, we cannot guarantee its absolute security.
                    </p>
                </section>

                <section>
                    <h2>5. Data Retention & Account Deletion</h2>
                    <p>
                        We retain your personal information for as long as your account is active or as needed
                        to provide you with our Services. We may also retain and use your information as necessary
                        to comply with our legal obligations, resolve disputes, and enforce our agreements.
                    </p>
                    <p>
                        When you delete your account: your username is anonymized, your email and credentials
                        are destroyed, your collections, settings, and notifications are removed, any open
                        trades are cancelled, and every session is logged out. Records of completed trades
                        (including their event logs and documentation photos) are retained in anonymized form
                        so the other participants keep an intact trade history and dispute record.
                    </p>
                </section>

                <section>
                    <h2>6. Your Rights and Choices</h2>
                    <p>Depending on your location, you may have certain rights regarding your personal information:</p>
                    <ul>
                        <li><strong>Access:</strong> You may request access to the personal information we hold about you.</li>
                        <li><strong>Correction:</strong> You may request that we correct inaccurate or incomplete information.</li>
                        <li><strong>Deletion:</strong> You may request deletion of your personal information, subject to certain exceptions.</li>
                        <li><strong>Data Portability:</strong> You may request a copy of your data in a structured, machine-readable format.</li>
                        <li><strong>Opt-Out:</strong> You may opt out of receiving promotional communications from us.</li>
                    </ul>
                    <p>To exercise any of these rights, please contact us at the email address provided below.</p>
                </section>

                <section>
                    <h2>7. Cookies and Tracking Technologies</h2>
                    <p>We use cookies and similar tracking technologies to collect and store information. These include:</p>
                    <ul>
                        <li><strong>Essential Cookies:</strong> Required for the operation of our Service, including authentication and session management.</li>
                        <li><strong>Preference Cookies:</strong> Used to remember your preferences, such as theme settings.</li>
                        <li><strong>Analytics Cookies:</strong> Set by Microsoft Clarity to recognize a returning browser so that usage statistics are not double-counted. They contain no personal information.</li>
                    </ul>
                    <p>
                        We do not use cookies for advertising purposes or share cookie data with third-party advertisers.
                        You may disable cookies through your browser settings; however, this may affect certain functionality of the Service.
                    </p>
                </section>

                <section>
                    <h2>8. Third-Party Services</h2>
                    <p>Our Service integrates with third-party services for card data and images:</p>
                    <ul>
                        <li>MTGJSON (mtgjson.com) for Magic: The Gathering card data</li>
                        <li>Pokemon TCG API (pokemontcg.io) for Pokemon card data</li>
                        <li>Scryfall (scryfall.com) for card images</li>
                    </ul>
                    <p>
                        These third-party services have their own privacy policies. We encourage you to review
                        their policies before using our Service.
                    </p>
                </section>

                <section>
                    <h2>9. Children's Privacy</h2>
                    <p>
                        Our Service is not intended for children under the age of 13. We do not knowingly collect
                        personal information from children under 13. If we become aware that we have collected
                        personal information from a child under 13 without verification of parental consent,
                        we will take steps to remove that information from our servers.
                    </p>
                </section>

                <section>
                    <h2>10. International Data Transfers</h2>
                    <p>
                        Your information may be transferred to and processed in countries other than your country
                        of residence. These countries may have data protection laws that differ from the laws of
                        your country. By using the Service, you consent to the transfer of your information to
                        such countries.
                    </p>
                </section>

                <section>
                    <h2>11. Changes to This Privacy Policy</h2>
                    <p>
                        We reserve the right to modify this Privacy Policy at any time. If we make material changes,
                        we will notify you by updating the "Effective Date" at the top of this policy and, where
                        appropriate, provide additional notice (such as via email or through the Service). Your
                        continued use of the Service after any changes indicates your acceptance of the updated policy.
                    </p>
                </section>

                <section>
                    <h2>12. Contact Us</h2>
                    <p>If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:</p>
                    <p>
                        <strong>Trade Spot</strong><br />
                        Email: <a href="mailto:support@thetraderspot.com">support@thetraderspot.com</a>
                    </p>
                </section>

                <section className="legal-acknowledgment">
                    <p>
                        <em>
                            By using Trade Spot, you acknowledge that you have read, understood, and agree to be
                            bound by this Privacy Policy.
                        </em>
                    </p>
                </section>
            </div>
        </div>
    );
}

export default PrivacyPolicy;
